import * as React from "react";
import { CommonHelper } from "../../../../../../../../react-core/Helpers/CommonHelper";
import { CheckboxEditor } from "../../../../../../../../react-core/Html/CheckboxEditor";
import { ToggleButtonTwoOptions } from "../../../../../../../../react-core/Html/ToggleButtonTwoOptions";
import { ReportContent } from "../../../../../../../../Wrap/Constants/Enums/Report/ReportContent";
import { ReportInsight } from "../../../../../../../../Wrap/Constants/Enums/Report/ReportInsight";
import { ReportIntroduction } from "../../../../../../../../Wrap/Constants/Enums/Report/ReportIntroduction";
import { ReportPerformanceGraph } from "../../../../../../../../Wrap/Constants/Enums/Report/ReportPerformanceGraph";
import { ReportTransaction } from "../../../../../../../../Wrap/Constants/Enums/Report/ReportTransaction";
import { ServiceProposition } from "../../../../../../../../Wrap/Constants/Enums/ServiceProposition";
import { ClientAccount } from "../../../../../../../../Wrap/Models/Domains/Account/SlAccounts/ClientAccount";
import { ReportBuilder } from "../../../../../../../../Wrap/Models/Models/Reports/ReportBuilder";
import { CheckboxGroup } from "../../../../../../../Shared/CheckboxGroup/CheckboxGroup";
import { Alert } from "../../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../../Shared/Entities/Constants/AlertConstants";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../../Shared/Process/Process/ProcessComponent";
import { UploadForm } from "../../../../../../../Site/Questions/PartComponents/UploadForm";
import { ImageStatus } from "../../../../../Settings/AddLogo/Resource/Content";
import { countNewLines } from "../../../../Common/Helpers/TextInputHelper";
import { ClientReportAction } from "../Action/ClientReportAction";
import {
    ReportContentMap,
    ReportInsightsArray,
    ReportInsightsMap,
    ReportPerformanceGraphArray,
    ReportPerformanceGraphMap,
    ReportTransactionsArray,
    ReportTransactionsMap,
} from "../Resource/Content";
import { ClientReportPortfolio } from "./ClientReportPortfolio";

interface IContentSettingsProps extends IProcessComponentProps {
    clientAccount: ClientAccount;
    data: ReportBuilder;
}

interface IContentSettingsState {
    signature: string;
    signatureUpdated: boolean;
    showErrorMsg: boolean;
    showIntroductionErrorMsg: boolean;
    showInsightsErrorMsg: boolean;
    showTransactionErrorMsg: boolean;
    showUploadImageButton: boolean;
    isValuationMoneyMovementsGraphEnabled: boolean;
    isReturnPerformanceGraphEnabled: boolean;
    isCumulativePerformanceGraphEnabled: boolean;
    isDiscretePerformanceGraphEnabled: boolean;
}

// New line will be calculated as 120 characters
const NEWLINE_SIZE = 120;

export class ContentSettings extends ProcessComponent<IContentSettingsProps, IContentSettingsState> {
    constructor(props: IContentSettingsProps) {
        super(props);
        this.state = {
            signature: null,
            signatureUpdated: false,
            showErrorMsg: false,
            showIntroductionErrorMsg: false,
            showInsightsErrorMsg: false,
            showTransactionErrorMsg: false,
            showUploadImageButton: false,
            isValuationMoneyMovementsGraphEnabled: true,
            isReturnPerformanceGraphEnabled: true,
            isCumulativePerformanceGraphEnabled: true,
            isDiscretePerformanceGraphEnabled: true,
        };
    }

    componentDidMount() {
        super.componentDidMount();
        ClientReportAction.getUserSignature(this.props.clientAccount.HierarchyId, (d) => {
            if (!CommonHelper.isNullOrEmpty(d)) {
                this.props.onChange({ SignatureUrl: d });
                this.setState({ signature: d });
            }
        });
        ClientReportAction.getReportSetting((d) => {
            this.setState({
                isValuationMoneyMovementsGraphEnabled: d.IsValuationMoneyMovementsGraphEnabled,
                isReturnPerformanceGraphEnabled: d.IsReturnPerformanceGraphEnabled,
                isCumulativePerformanceGraphEnabled: d.IsCumulativePerformanceGraphEnabled,
                isDiscretePerformanceGraphEnabled: d.IsDiscretePerformanceGraphEnabled,
            });
        });
    }

    isValid() {
        const flag = super.isValid();
        let isValid = flag;
        if (
            (this.props.data?.ReportContents || []).includes(ReportContent.ReportIntroduction) &&
            this.props.data?.ReportIntroductions.length == 0
        ) {
            this.setState({ showIntroductionErrorMsg: true });
            isValid = false;
        }

        if (
            (this.props.data?.ReportContents || []).includes(ReportContent.Insights) &&
            this.props.data?.ReportInsights.length == 0
        ) {
            this.setState({ showInsightsErrorMsg: true });
            isValid = false;
        }

        if (
            (this.props.data?.ReportContents || []).includes(ReportContent.Transactions) &&
            this.props.data?.ReportTransactions.length == 0
        ) {
            this.setState({ showTransactionErrorMsg: true });
            isValid = false;
        }

        if (
            (this.props.data?.ReportIntroductions || []).includes(ReportIntroduction.Signature) &&
            CommonHelper.isNullOrEmpty(this.props.data?.SignatureUrl)
        ) {
            this.setState({ showErrorMsg: true });
            return false;
        }
        return isValid;
    }

    private renderContentHeader(
        content: ReportContent,
        info: string,
        icon: string,
        disabled: boolean,
        child?: JSX.Element,
        errorMessage?: string,
        contentProps?: { colSize?: number }
    ) {
        const { ReportContents } = this.props.data;
        let isOn = ReportContents.includes(content);
        if (disabled) {isOn = false;}
        return (
            <>
                <div className="row">
                    <div className={`col-12 col-sm-7 ${!isOn ? "is-off" : ""}`}>
                        <h3 className="section-title mb-6">
                            <span className={icon} aria-hidden="true"></span>
                            <span className="text">{ReportContentMap[content]}</span>
                        </h3>
                        <p>{info}</p>
                    </div>
                    <div className="col-12 col-sm-5 col-right">
                        <ToggleButtonTwoOptions
                            optionOne={isOn ? "" : "Off"}
                            optionTwo={isOn ? "On" : ""}
                            value={isOn}
                            disabled={disabled}
                            onChange={(v) => {
                                this.setList(v, "ReportContents", content);
                                if (!v && errorMessage != null) {
                                    this.state[errorMessage] = false;
                                }
                            }}
                        />
                    </div>
                </div>
                {isOn && (
                    <div className="row">
                        <div className={`col-12 col-sm-${contentProps?.colSize || 7}`}>{child}</div>
                    </div>
                )}
            </>
        );
    }

    private setList(
        v: boolean,
        key: string,
        content: ReportIntroduction | ReportContent | ReportPerformanceGraph | ReportInsight | ReportTransaction
    ) {
        let _list = this.props.data[key].concat();
        if (v) {
            _list.push(content);
        } else {
            _list = _list.filter((ele) => ele != content);
        }
        this.props.onChange({ [key]: _list });
    }

    private renderReportIntroduction() {
        const { ReportIntroductions } = this.props.data;
        const { data, onChange } = this.props;
        const commentaryMaxSize = 600;
        const signatureMaxSize = { width: 350, height: 150 };

        return (
            <fieldset className="form-group checkbox-layout-vertical mb-11">
                <legend className="control-label sr-only">What to include?</legend>
                <div className="controls-wrap">
                    <CheckboxEditor
                        label="Include cover page with firm logo"
                        cssWrapper="square-checkbox "
                        value={ReportIntroductions.includes(ReportIntroduction.CoverPage)}
                        onChange={(v) => {
                            this.setList(v, "ReportIntroductions", ReportIntroduction.CoverPage);
                            if (v) {
                                this.setState({ showIntroductionErrorMsg: false });
                            }
                        }}
                    />
                    <CheckboxEditor
                        label="Include contents page"
                        cssWrapper="square-checkbox "
                        value={ReportIntroductions.includes(ReportIntroduction.ContentsPage)}
                        onChange={(v) => {
                            this.setList(v, "ReportIntroductions", ReportIntroduction.ContentsPage);
                            if (v) {
                                this.setState({ showIntroductionErrorMsg: false });
                            }
                        }}
                    />
                    <CheckboxEditor
                        label="Add commentary"
                        cssWrapper="square-checkbox "
                        value={ReportIntroductions.includes(ReportIntroduction.Commentary)}
                        onChange={(v) => {
                            this.setList(v, "ReportIntroductions", ReportIntroduction.Commentary);
                            if (v) {
                                this.setState({ showIntroductionErrorMsg: false });
                            }
                        }}
                    />
                    {ReportIntroductions.includes(ReportIntroduction.Commentary) && (
                        <div className="controls-textarea">
                            <textarea
                                rows={5}
                                maxLength={commentaryMaxSize}
                                value={data?.Commentary}
                                onKeyDown={(event) => {
                                    const isEnter = event.key === "Enter" || (event.keyCode || event.which) === 13;
                                    if (isEnter) {
                                        const count = countRemainingCharacters(data?.Commentary, commentaryMaxSize);
                                        if (count < NEWLINE_SIZE) {
                                            // when remaining characaters are less than a new line,
                                            // prevent user from pressing enter key to create one.
                                            event.preventDefault();
                                        }
                                    }
                                }}
                                onChange={(event) => {
                                    const value = event.target.value;
                                    onChange({ Commentary: trimContent(value, commentaryMaxSize) });
                                }}
                            ></textarea>
                            <div>
                                You have {countRemainingCharacters(data?.Commentary, commentaryMaxSize)} characters
                                remaining.
                            </div>
                        </div>
                    )}

                    <CheckboxEditor
                        label="Upload image"
                        cssWrapper="square-checkbox "
                        value={ReportIntroductions.includes(ReportIntroduction.Signature)}
                        onChange={(v) => {
                            this.setList(v, "ReportIntroductions", ReportIntroduction.Signature);
                            if (v) {
                                this.setState({ showIntroductionErrorMsg: false });
                            }
                        }}
                    />
                    {ReportIntroductions.includes(ReportIntroduction.Signature) && (
                        <div className="col-12 col-sm-8">
                            <UploadForm
                                imageStatus={this.state.signature ? ImageStatus.ImageChosen : ImageStatus.None}
                                imageBlobData={this.state.signature}
                                cssWrapper="mb-50"
                                maxSize={signatureMaxSize}
                                fileSizeAlertMessage={`Image size cannot exceed ${signatureMaxSize.width} x ${signatureMaxSize.height} pixels.`}
                                getImageBlobData={(val) => {
                                    this.setState({
                                        signature: val,
                                        signatureUpdated: false,
                                        showUploadImageButton: true,
                                    });
                                }}
                                onSelectAnotherChage={() => {
                                    this.setState({
                                        signature: null,
                                        signatureUpdated: false,
                                        showUploadImageButton: false,
                                    });
                                }}
                            />
                            <p>
                                Maximum image size: {signatureMaxSize.width} x {signatureMaxSize.height} pixels.
                            </p>
                            {this.state.showUploadImageButton && (
                                <button
                                    className="btn primary"
                                    onClick={() => {
                                        if (this.state.signature) {
                                            ClientReportAction.setUserSignature(
                                                this.state.signature,
                                                this.props.clientAccount.HierarchyId,
                                                (d) => {
                                                    this.setState({
                                                        signatureUpdated: true,
                                                        showUploadImageButton: false,
                                                    });
                                                    this.props.onChange({ SignatureUrl: d });
                                                }
                                            );
                                        }
                                    }}
                                >
                                    Upload image
                                </button>
                            )}
                            {this.state.signatureUpdated && (
                                <p className="alert-success small">Image uploaded successfully</p>
                            )}
                            {this.state.showErrorMsg && (
                                <Alert alertType={AlertType.Error} message={"Please upload the image"} />
                            )}
                        </div>
                    )}
                </div>
            </fieldset>
        );
    }

    private renderList(
        array: number[],
        map: Record<number, string>,
        key: string,
        title: string,
        errorMessage: string,
        disabled?: boolean
    ) {
        const { data } = this.props;
        const options = array.map((ele) => {
            return {
                label: map[ele],
                cssWrapper: "square-checkbox ",
                value: data[key].includes(ele),
                onChange: (v) => {
                    this.setList(v, key, ele);
                    if (v && errorMessage != null) {
                        this.state[errorMessage] = false;
                    }
                },
                disabled: disabled,
            };
        });

        return (
            <fieldset className="form-group checkbox-layout-vertical mb-0">
                <CheckboxGroup title={<b>{title}</b>} options={options} type="block" />
            </fieldset>
        );
    }

    render() {
        const { clientAccount } = this.props;
        const isFundzone = clientAccount.AccountServicePropositionId == ServiceProposition.Fundzone;
        const reportGraphArray = [];
        const reportGraphMap = {};
        if (this.state.isValuationMoneyMovementsGraphEnabled) {
            reportGraphArray.push(ReportPerformanceGraph.ValuationMoneyMovements);
            reportGraphMap[ReportPerformanceGraph.ValuationMoneyMovements] = "Valuation and money movements";
        }
        if (this.state.isReturnPerformanceGraphEnabled) {
            reportGraphArray.push(ReportPerformanceGraph.ReturnPerformance);
            reportGraphMap[ReportPerformanceGraph.ReturnPerformance] = "Return performance";
        }
        if (this.state.isCumulativePerformanceGraphEnabled) {
            reportGraphArray.push(ReportPerformanceGraph.CumulativePerformance);
            reportGraphMap[ReportPerformanceGraph.CumulativePerformance] = "Cumulative performance";
        }
        if (this.state.isDiscretePerformanceGraphEnabled) {
            reportGraphArray.push(ReportPerformanceGraph.DiscretePerformance);
            reportGraphMap[ReportPerformanceGraph.DiscretePerformance] = "Discrete performance";
        }
        return (
            <ClientReportPortfolio cssWrapper="contents">
                <div className="row">
                    <div className="col-12 col-sm-7">
                        <h2>Contents</h2>
                    </div>
                    <div className="col-12 col-sm-5 col-right">
                        <legend className="include-report">
                            <p>Include in report</p>
                        </legend>
                    </div>
                </div>

                {/* report introduction */}
                {this.renderContentHeader(
                    ReportContent.ReportIntroduction,
                    "This will show report cover page, contents and commentary",
                    "report-icon",
                    false,
                    this.renderReportIntroduction(),
                    "showIntroductionErrorMsg",
                    { colSize: 11 }
                )}
                {this.state.showIntroductionErrorMsg && (
                    <Alert
                        alertType={AlertType.Error}
                        message={"Please select at least one Report Introduction option"}
                    />
                )}
                <hr />

                {/* account detail */}
                {this.renderContentHeader(
                    ReportContent.AccountDetails,
                    "This will show a summary of your product instructions",
                    "account-details-icon",
                    false
                )}
                <hr />

                {/* snapshot */}
                {this.renderContentHeader(
                    ReportContent.Snapshot,
                    "This will show a summary of the headline figures from the portfolio",
                    "snapshot-icon",
                    false
                )}
                <hr />

                {/* valuation */}
                {this.renderContentHeader(
                    ReportContent.Valuation,
                    "This will show a summary of the portfolio valuation",
                    "valuation-icon",
                    false
                )}
                <hr />

                {/* performance */}
                {this.renderContentHeader(
                    ReportContent.Performance,
                    `This will show a summary of the portfolio opening and closing values, money movements, and monetary and percentage change. ${
                        !isFundzone ? "It also includes a range of performance related graphs" : ""
                    }`,
                    "performance-icon",
                    false,
                    !isFundzone
                        ? this.renderList(
                              reportGraphArray,
                              reportGraphMap,
                              "ReportPerformanceGraphs",
                              "Graphs",
                              null,
                              isFundzone
                          )
                        : null,
                    null
                )}
                {!isFundzone && <hr />}

                {/* insights */}
                {!isFundzone &&
                    this.renderContentHeader(
                        ReportContent.Insights,
                        "This will show where the portfolio investments are held",
                        "insights-icon",
                        isFundzone,
                        this.renderList(
                            ReportInsightsArray,
                            ReportInsightsMap,
                            "ReportInsights",
                            "",
                            "showInsightsErrorMsg"
                        ),
                        "showInsightsErrorMsg"
                    )}
                {this.state.showInsightsErrorMsg && (
                    <Alert alertType={AlertType.Error} message={"Please select at least one Insights option"} />
                )}
                {!isFundzone && <hr />}

                {/* transactions */}
                {!isFundzone &&
                    this.renderContentHeader(
                        ReportContent.Transactions,
                        "This will show a summary of the portfolio money paid in, money taken out and charges",
                        "transactions-icon",
                        isFundzone,
                        this.renderList(
                            ReportTransactionsArray,
                            ReportTransactionsMap,
                            "ReportTransactions",
                            "",
                            "showTransactionErrorMsg"
                        ),
                        "showTransactionErrorMsg"
                    )}
                {this.state.showTransactionErrorMsg && (
                    <Alert alertType={AlertType.Error} message={"Please select at least one Transactions option"} />
                )}
            </ClientReportPortfolio>
        );
    }
}

function countRemainingCharacters(content: string, maxChars: number) {
    if (typeof content !== "string") {
        return maxChars;
    }
    const newLines = countNewLines(content);
    const length = content.length + newLines.count * (NEWLINE_SIZE - newLines.newLineCharacters.length);
    return Math.max(0, maxChars - length);
}

// When user enter or paste the content with more than the max number characters,
// trim the content to fit.
function trimContent(content: string, maxChars: number): string {
    if (typeof content !== "string") {
        return content;
    }
    const newLines = countNewLines(content);
    const length = content.length + newLines.count * (NEWLINE_SIZE - newLines.newLineCharacters.length);
    if (maxChars >= length) {
        return content;
    }
    if (newLines.count === 0) {
        return content.length > maxChars ? content.slice(0, maxChars) : content;
    }
    const lines: string[] = content.split(newLines.newLineCharacters);
    const trimmedLines: string[] = [];
    let resizeLength = 0;
    for (const line of lines) {
        if (resizeLength >= maxChars) {
            break;
        }
        if (resizeLength + line.length > maxChars) {
            const remainingLength = maxChars - resizeLength;
            trimmedLines.push(line.slice(0, remainingLength));
            resizeLength = maxChars;
        } else {
            trimmedLines.push(line);
            resizeLength += line.length + newLines.newLineCharacters.length * NEWLINE_SIZE;
        }
    }

    return trimmedLines.join(newLines.newLineCharacters);
}
